/*
 * /prescription/$patientId (page)
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Container,
	Page,
	Placeholder,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { Plus, X } from "lucide-react"
import { PrescriptionForm } from "../../features/prescription/cannabis/prescription-form"
import { HistoricPrescriptions } from "../../features/prescription/cannabis/historic-prescriptions"
import { PatientHeader } from "../../features/patient/patient-header"
import { trpc } from "../../trpc"

export const Route = createFileRoute("/prescription/$patientId")({
	component: PrescriptionPage,
})

const MOCK_PATIENT = {
	firstName: "...",
	lastName: "",
	dateOfBirth: null,
	bloodType: null,
	allergies: null,
	emergencyContactName: null,
	emergencyContactPhone: null,
}

function PrescriptionPage() {
	const { patientId } = Route.useParams()
	const [isDirty, setIsDirty] = useState(false)
	const [isCreatingNew, setIsCreatingNew] = useState(false)

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	const { data: practitioner, isLoading: practitionerLoading } =
		trpc.practitioner.getByFilter.useQuery({ where: {} })

	const { data: prescription, isLoading: prescriptionLoading } =
		trpc.prescription.getByFilter.useQuery({
			where: { patientId, isLatest: true, active: true },
		})

	const { data: existingLpPrescriptions, isLoading: lpLoading } =
		trpc.lpPrescription.getAllByFilter.useQuery(
			{ where: { prescriptionId: prescription?.id, active: true } },
			{ enabled: !!prescription?.id },
		)

	const isLoading = patientLoading || practitionerLoading || prescriptionLoading || lpLoading
	const practitionerId = practitioner?.id ?? ""
	const displayPatient = patient ?? (MOCK_PATIENT as any)

	function handleNewSuccess() {
		setIsCreatingNew(false)
		setIsDirty(false)
	}

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={isDirty} />
			<Container width="xl" padding="lg" gap="lg">
				{isLoading ? (
					<Placeholder title="Loading patient data..." />
				) : prescription ? (
					<>
						{/* New prescription form — on top when creating */}
						{isCreatingNew && (
							<Card>
								<CardHeader>
									<div className="flex items-center justify-between">
										<div>
											<CardTitle>New Prescription</CardTitle>
											<CardDescription>
												Saving will archive the current prescription and activate this one.
											</CardDescription>
										</div>
										<Button
											size="sm"
											variant="ghost"
											onClick={() => {
												setIsCreatingNew(false)
												setIsDirty(false)
											}}
										>
											<X className="w-4 h-4 mr-1" />
											Cancel
										</Button>
									</div>
								</CardHeader>
								<CardContent>
									<PrescriptionForm
										patientId={patientId}
										practitionerId={practitionerId}
										onDirtyChange={setIsDirty}
										onSuccess={handleNewSuccess}
									/>
								</CardContent>
							</Card>
						)}

						{/* Active prescription — read-only */}
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<div>
										<CardTitle>Active Prescription</CardTitle>
										{prescription.startDate && (
											<CardDescription>
												Started{" "}
												{new Date(prescription.startDate).toLocaleDateString("en-CA", {
													year: "numeric",
													month: "short",
													day: "numeric",
												})}
												{prescription.endDate && (
													<>
														{" "}· Expires{" "}
														{new Date(prescription.endDate).toLocaleDateString("en-CA", {
															year: "numeric",
															month: "short",
															day: "numeric",
														})}
													</>
												)}
											</CardDescription>
										)}
									</div>
									<div className="flex items-center gap-2">
										<Badge variant="secondary">Active</Badge>
										{!isCreatingNew && (
											<Button
												size="sm"
												variant="outline"
												onClick={() => setIsCreatingNew(true)}
											>
												<Plus className="w-4 h-4 mr-1" />
												New Prescription
											</Button>
										)}
									</div>
								</div>
							</CardHeader>
							<CardContent>
								<PrescriptionForm
									patientId={patientId}
									practitionerId={practitionerId}
									prescription={prescription}
									existingLpPrescriptions={existingLpPrescriptions ?? undefined}
									disabled={true}
								/>
							</CardContent>
						</Card>
					</>
				) : (
					/* No existing prescription — show create form */
					<Card>
						<CardHeader>
							<CardTitle>New Prescription</CardTitle>
						</CardHeader>
						<CardContent>
							<PrescriptionForm
								patientId={patientId}
								practitionerId={practitionerId}
								onDirtyChange={setIsDirty}
							/>
						</CardContent>
					</Card>
				)}
				{!isLoading && <HistoricPrescriptions patientId={patientId} />}
			</Container>
		</Page>
	)
}
